import { useEffect, useState } from "react";

type Options = {
  /**
   * How close (in px) to the bottom counts as "at bottom".
   * Helps avoid flicker due to fractional pixels / layout shifts.
   */
  thresholdPx?: number;
};

export function useIsAtPageBottom(options: Options = {}) {
  const { thresholdPx = 24 } = options;
  const [isAtBottom, setIsAtBottom] = useState(false);

  useEffect(() => {
    let raf = 0;

    const compute = () => {
      const docEl = document.documentElement;
      const scrollTop = window.scrollY ?? docEl.scrollTop ?? 0;
      const viewportH = window.innerHeight ?? docEl.clientHeight ?? 0;
      const scrollH = docEl.scrollHeight ?? 0;

      // If the page doesn't scroll, this correctly returns true.
      const atBottom = scrollTop + viewportH >= scrollH - thresholdPx;
      setIsAtBottom(atBottom);
    };

    const onScrollOrResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };

    compute();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [thresholdPx]);

  return isAtBottom;
}

